'use client';

import { type ReactNode } from 'react';
import { usePreviousIds } from '../../../../hooks/usePreviousIds';

interface BattleItem {
  id: string;
}

interface AnimatedBattleListProps<T extends BattleItem> {
  battles: T[] | undefined;
  renderBattle: (battle: T) => ReactNode;
  renderEmpty?: () => ReactNode;
  className?: string;
}

/**
 * Animated wrapper for battle list items. CSS keyframe enter
 * animation only on genuinely new ids (post-first-render); removed
 * items disappear instantly. Drop-in replacement for the previous
 * framer-motion AnimatePresence implementation.
 */
export function AnimatedBattleList<T extends BattleItem>({
  battles,
  renderBattle,
  renderEmpty,
  className,
}: AnimatedBattleListProps<T>) {
  const { addedIds, isFirstRender } = usePreviousIds(battles);

  const isEmpty = !battles || battles.length === 0;

  return (
    <div className={`flex flex-col gap-2 ${className || ''}`}>
      {battles?.map((battle) => {
        const shouldAnimate = !isFirstRender && addedIds.has(battle.id);
        return (
          <div
            key={battle.id}
            className={shouldAnimate ? 'animate-shot-battle-item-in' : undefined}
          >
            {renderBattle(battle)}
          </div>
        );
      })}
      {isEmpty && renderEmpty?.()}
    </div>
  );
}
